import { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronsUpDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DEFAULT_UNIT_LABELS } from '@jobos/shared';

interface UnitLabelInputProps {
  value: string;
  onChange: (value: string) => void;
  // Suggestions shown in the dropdown; a custom value can always be typed.
  suggestions?: string[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

// A compact combobox for a qty×rate unit label: pick from the suggestions or
// type a custom one. Opening always shows the full list (unlike a native
// <datalist>, which hides options once the field has text). Self-contained so
// it doesn't change the shared FreestyleCombobox used by other forms.
export function UnitLabelInput({
  value,
  onChange,
  suggestions = DEFAULT_UNIT_LABELS,
  placeholder = 'unit',
  disabled = false,
  className,
}: UnitLabelInputProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  // On open, clear the search so the full list shows, and match the popover
  // width to the (often narrow) trigger.
  useEffect(() => {
    if (open) {
      setSearch('');
      if (buttonRef.current) setWidth(buttonRef.current.offsetWidth);
    }
  }, [open]);

  const options = useMemo(() => Array.from(new Set(suggestions)), [suggestions]);
  const filtered = options.filter((o) => o.toLowerCase().includes(search.toLowerCase()));
  const isExact = options.some((o) => o.toLowerCase() === search.trim().toLowerCase());

  const choose = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={buttonRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal px-2', !value && 'text-muted-foreground', className)}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{ width: width ? `${width}px` : undefined, minWidth: '9rem' }}
      >
        <Command shouldFilter={false}>
          <CommandInput placeholder="Pick or type a unit…" value={search} onValueChange={setSearch} />
          <CommandList>
            {filtered.length === 0 && !search.trim() && <CommandEmpty>No units.</CommandEmpty>}
            <CommandGroup>
              {filtered.map((o) => (
                <CommandItem key={o} value={o} onSelect={() => choose(o)}>
                  <Check className={cn('mr-2 h-4 w-4', value === o ? 'opacity-100' : 'opacity-0')} />
                  {o}
                </CommandItem>
              ))}
              {search.trim() && !isExact && (
                <CommandItem value={search} onSelect={() => choose(search.trim())} className="text-primary">
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  Use "{search.trim()}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
